import { ApiClient, type AuthMe, type Application } from './lib/api.js';
import { clear, el, mount } from './lib/dom.js';
import { renderLogin } from './views/login.js';
import { renderBranches } from './views/branches.js';
import { renderDeploy } from './views/deploy.js';
import { renderLogs } from './views/logs.js';
import { renderMonitoring } from './views/monitoring.js';
import { renderRunpodImages } from './views/runpod-images.js';
import { renderRunpodCustomNodes } from './views/runpod-custom-nodes.js';
import { renderRunpodCustomModels } from './views/runpod-custom-models.js';
import { renderEnvFile } from './views/env-file.js';
import { renderAccounts } from './views/accounts.js';
import { renderInvitations } from './views/invitations.js';
import { openApplicationsAdmin } from './views/applications-admin.js';
import { openApiKeysAdmin } from './views/api-keys-admin.js';

type Tab =
  | 'branches'
  | 'env'
  | 'deploy'
  | 'logs'
  | 'monitoring'
  | 'accounts'
  | 'invitations'
  | 'runpod-images'
  | 'runpod-custom-nodes'
  | 'runpod-custom-models';

interface TabSpec {
  id: Tab;
  label: string;
  /** Short label shown inside a group menu (defaults to label). */
  menuLabel?: string;
  /** Tabs sharing a group id collapse into one hover dropdown. */
  group?: 'runpod';
  /** When set, the tab only shows for apps the predicate accepts. */
  enabled?: (app: Application) => boolean;
}

const TABS: TabSpec[] = [
  { id: 'branches', label: 'Branches' },
  { id: 'env', label: 'Environment' },
  { id: 'deploy', label: 'Deploy' },
  { id: 'logs', label: 'History' },
  { id: 'monitoring', label: 'Monitoring' },
  { id: 'accounts', label: 'Accounts', enabled: (a) => a.capabilities.identityAdmin },
  // Same capability gate as Accounts: both reach the IdP admin API through
  // the service token, so if one is unavailable the other is too.
  { id: 'invitations', label: 'Invitations', enabled: (a) => a.capabilities.identityAdmin },
  { id: 'runpod-images', label: 'Runpod Endpoints', menuLabel: 'Endpoints', group: 'runpod', enabled: (a) => a.capabilities.runpod },
  { id: 'runpod-custom-nodes', label: 'Custom Nodes', menuLabel: 'Custom Nodes', group: 'runpod', enabled: (a) => a.capabilities.runpod },
  { id: 'runpod-custom-models', label: 'Custom Models', menuLabel: 'Custom Models', group: 'runpod', enabled: (a) => a.capabilities.runpod },
];

class App {
  private readonly root: HTMLElement;
  private readonly api = new ApiClient();
  private currentTab: Tab = 'branches';
  private apps: Application[] = [];
  private currentApp: Application | null = null;
  private deployView: HTMLElement | null = null;
  private logsView: HTMLElement | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
  }

  async start(): Promise<void> {
    try {
      const me = await this.api.me();
      if (me) await this.showDashboard(me);
      else this.showLogin();
    } catch {
      this.showLogin();
    } finally {
      this.root.removeAttribute('aria-busy');
    }
  }

  private showLogin(): void {
    mount(this.root, [
      renderLogin({
        api: this.api,
        onSuccess: (me) => void this.showDashboard(me),
      }),
    ]);
  }

  private visibleTabs(app: Application): TabSpec[] {
    return TABS.filter((t) => !t.enabled || t.enabled(app));
  }

  private async showDashboard(me: AuthMe): Promise<void> {
    try {
      const { applications } = await this.api.listApplications();
      this.apps = applications;
    } catch {
      this.apps = [];
    }
    if (this.apps.length === 0) {
      mount(this.root, [
        el('div', { class: 'shell', children: [
          el('div', { class: 'card', children: [
            el('h2', { text: 'No applications configured' }),
            el('p', { text: 'The applications list could not be loaded.' }),
          ] }),
        ] }),
      ]);
      return;
    }

    // Default to the first app (blueeden-app, sort_order 1) unless we already
    // had one selected that still exists.
    const previous = this.currentApp && this.apps.find((a) => a.id === this.currentApp!.id);
    this.currentApp = previous ?? this.apps[0]!;
    this.api.setCurrentApp(this.currentApp.id);

    const tabBar = el('div', { class: 'tabs', attrs: { role: 'tablist' } });
    const content = el('div');

    // Reflect the active tab on both flat tab buttons and group triggers.
    const applySelection = () => {
      for (const node of tabBar.querySelectorAll('[data-tab]')) {
        (node as HTMLElement).setAttribute('aria-selected', String((node as HTMLElement).dataset.tab === this.currentTab));
      }
      for (const trig of tabBar.querySelectorAll('[data-group]')) {
        const g = (trig as HTMLElement).dataset.group;
        (trig as HTMLElement).setAttribute('aria-selected', String(TABS.some((t) => t.group === g && t.id === this.currentTab)));
      }
    };

    const renderTab = (tab: Tab) => {
      const app = this.currentApp!;
      this.currentTab = tab;
      applySelection();
      if (tab === 'branches') {
        mount(content, [
          renderBranches({
            api: this.api,
            onDeployRequested: (branch) => {
              renderTab('deploy');
              const dv = this.deployView as HTMLElement & { setBranch?: (b: string) => void };
              dv?.setBranch?.(branch);
            },
          }),
        ]);
      } else if (tab === 'env') {
        mount(content, [
          renderEnvFile({
            api: this.api,
            onDeployRequested: () => renderTab('deploy'),
          }),
        ]);
      } else if (tab === 'deploy') {
        this.deployView = renderDeploy({
          api: this.api,
          onDeploymentFinished: () => {
            // If user switches to logs tab next, it'll refresh on mount.
          },
        });
        mount(content, [this.deployView]);
      } else if (tab === 'logs') {
        this.logsView = renderLogs({ api: this.api });
        mount(content, [this.logsView]);
      } else if (tab === 'accounts') {
        mount(content, [renderAccounts({ api: this.api })]);
      } else if (tab === 'invitations') {
        mount(content, [renderInvitations({ api: this.api })]);
      } else if (tab === 'runpod-images') {
        mount(content, [renderRunpodImages({
          api: this.api,
          volumeEnabled: me.features?.runpodVolume === true,
        })]);
      } else if (tab === 'runpod-custom-nodes') {
        mount(content, [renderRunpodCustomNodes({ api: this.api })]);
      } else if (tab === 'runpod-custom-models') {
        mount(content, [renderRunpodCustomModels({ api: this.api })]);
      } else {
        mount(content, [renderMonitoring({ api: this.api, proxyMonitoring: app.proxyMonitoring })]);
      }
    };

    const GROUP_LABELS: Record<string, string> = { runpod: 'Runpod' };
    const buildGroup = (groupId: string, groupTabs: TabSpec[]): HTMLElement => {
      const wrap = el('div', { class: 'tab-group' });
      const trigger = el('button', {
        class: 'tab tab-group-trigger',
        text: `${GROUP_LABELS[groupId] ?? groupId} ▾`,
        attrs: { type: 'button', 'data-group': groupId, 'aria-haspopup': 'true', 'aria-selected': false },
        on: { click: () => wrap.classList.toggle('open') },
      });
      const menu = el('div', { class: 'tab-group-menu', attrs: { role: 'menu' } });
      for (const st of groupTabs) {
        menu.append(el('button', {
          class: 'tab-menu-item',
          text: st.menuLabel ?? st.label,
          attrs: { type: 'button', role: 'menuitem', 'data-tab': st.id, 'aria-selected': false },
          on: { click: () => { wrap.classList.remove('open'); trigger.blur(); renderTab(st.id); } },
        }));
      }
      wrap.append(trigger, menu);
      return wrap;
    };

    const buildTabBar = () => {
      clear(tabBar);
      const tabs = this.visibleTabs(this.currentApp!);
      // Keep the active tab if it's still available for this app, else reset.
      if (!tabs.some((t) => t.id === this.currentTab)) this.currentTab = 'branches';
      const doneGroups = new Set<string>();
      for (const t of tabs) {
        if (t.group) {
          if (doneGroups.has(t.group)) continue;
          doneGroups.add(t.group);
          tabBar.append(buildGroup(t.group, tabs.filter((x) => x.group === t.group)));
        } else {
          tabBar.append(el('button', {
            class: 'tab',
            text: t.label,
            attrs: { type: 'button', role: 'tab', 'data-tab': t.id, 'aria-selected': false },
            on: { click: () => renderTab(t.id) },
          }));
        }
      }
      applySelection();
    };

    const appSelect = el('select', {
      class: 'app-select',
      attrs: { 'aria-label': 'Application' },
    }) as HTMLSelectElement;
    for (const a of this.apps) {
      appSelect.append(el('option', { text: a.name, attrs: { value: String(a.id) } }));
    }
    appSelect.value = String(this.currentApp.id);
    appSelect.addEventListener('change', () => {
      const next = this.apps.find((a) => a.id === Number(appSelect.value));
      if (!next) return;
      this.currentApp = next;
      this.api.setCurrentApp(next.id);
      buildTabBar();
      renderTab(this.currentTab);
    });

    const manageBtn = el('button', {
      class: 'ghost app-manage',
      text: '⚙',
      attrs: { type: 'button', title: 'Manage applications', 'aria-label': 'Manage applications' },
      on: { click: () => openApplicationsAdmin({ api: this.api, onChanged: () => void this.showDashboard(me) }) },
    });

    const apiKeysBtn = el('button', {
      class: 'ghost app-manage',
      text: '🔑',
      attrs: { type: 'button', title: 'API keys', 'aria-label': 'API keys' },
      on: { click: () => openApiKeysAdmin({ api: this.api }) },
    });

    const logoutBtn = el('button', {
      class: 'ghost',
      text: 'Sign out',
      on: {
        click: async () => {
          try {
            await this.api.logout();
          } finally {
            this.showLogin();
          }
        },
      },
    });

    const topbar = el('div', {
      class: 'topbar',
      children: [
        el('div', {
          class: 'brand',
          children: [
            el('span', { class: 'brand-dot' }),
            el('span', { text: 'Blue Eden Deploy' }),
          ],
        }),
        el('div', { class: 'app-switch', children: [
          el('span', { class: 'app-switch-label', text: 'App' }),
          appSelect,
          manageBtn,
          apiKeysBtn,
        ] }),
        el('div', {
          class: 'who',
          children: [
            el('span', { text: me.user.username }),
            el('span', { text: ' · ' }),
            logoutBtn,
          ],
        }),
      ],
    });

    const shell = el('div', {
      class: 'shell',
      children: [topbar, tabBar, content],
    });

    clear(this.root);
    this.root.append(shell);
    buildTabBar();
    renderTab(this.currentTab);
  }
}

const root = document.getElementById('app');
if (!root) throw new Error('Missing #app element');
const app = new App(root);
void app.start();
